import { styled } from "@mui/material/styles";
import { Box, Typography, Button, Tab, Tabs } from "@mui/material";
import SuccessPaymentIcon from "@assets/svg/SuccessPayment.svg?react";
import { ArrowBack } from "@mui/icons-material";
import MsoolVector from "@assets/svg/msoolVector.svg?react";
import FailPayment from "@assets/svg/FailPayment.svg?react";
import ReTry from "@assets/svg/ReTry.svg?react";
import GuestPortalDetailsIconSvg from "@assets/svg/GuestPortalDetails.svg?react";

interface ContainerProps {
  $isPhone: boolean;
}

interface TextProps {
  $isPhone: boolean;
}

interface ButtonProps {
  $isPhone: boolean;
}

interface BadgeProps {
  $isPhone: boolean;
}

// ==============================
// Main Portal Container Styles
// ==============================

export const PortalMainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: "80px",
  maxWidth: "100%",
  margin: "0 auto",
  [theme.breakpoints.up('md')]: {
    maxWidth: "100%",
    paddingLeft: "20px",
    paddingRight: "20px"
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: "100%"
  }
}));

export const LogoContainer = styled(Box)(({ theme }) => ({
  marginTop: "56px",
  marginBottom: "56px",
  objectFit: "contain",
  cursor: "pointer",
  display: "flex",
  justifyContent: "center",
  [theme.breakpoints.up('md')]: {
    marginTop: "40px",
    marginBottom: "40px"
  }
}));

// ==============================
// Welcome Step Styles
// ==============================

export const WelcomeContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  [theme.breakpoints.up('md')]: {
    maxWidth: "450px",
    margin: "0 auto"
  }
}));

export const WelcomeTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "36px",
  color: "#331860",
  textAlign: "center",
  [theme.breakpoints.up('md')]: {
    fontSize: "32px",
    lineHeight: "48px"
  }
}));

export const WelcomeDescription = styled(Typography)(({ theme }) => ({
  marginTop: "16px",
  marginBottom: "16px",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "19px",
  color: "#331860",
  textAlign: "center",
  [theme.breakpoints.up('md')]: {
    fontSize: "18px",
    lineHeight: "24px",
    marginTop: "24px",
    marginBottom: "24px"
  }
}));

export const WelcomeButton = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  marginRight: "auto",
  marginLeft: "auto",
  marginTop: "56px",
  color: "white",
  fontWeight: 700,
  lineHeight: "24px",
  fontSize: "16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "48px",
  width: "95%",
  backgroundColor: "#5B3FFF",
  borderRadius: "8px",
  [theme.breakpoints.up('md')]: {
    height: "56px",
    fontSize: "18px",
    marginTop: "40px",
    maxWidth: "350px"
  }
}));

// ==============================
// Guest Portal Details Styles
// ==============================

export const GuestPortalContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    margin: "0 auto",
    padding: "20px"
  }
}));

// Desktop Layout Components
export const DesktopLayoutContainer = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up('md')]: {
    display: "flex",
    width: "100%",
    gap: "16px",
    margin: "0 auto",
    padding: "20px",
    maxWidth: "1440px"
  }
}));

export const DesktopImageSection = styled(Box)(({ theme }) => ({
  flex: "0 0 60%",
  display: "flex",
  flexDirection: "column",
  gap: "16px"
}));

export const DesktopContentSection = styled(Box)(({ theme }) => ({
  flex: "0 0 40%",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  paddingLeft: "20px"
}));

export const DesktopMainImage = styled('img')(({ theme }) => ({
  width: "100%",
  height: "400px",
  objectFit: "cover",
  borderRadius: "12px"
}));

export const DesktopImageGallery = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "8px",
  marginTop: "8px"
}));

export const DesktopGalleryImage = styled('img')(({ theme }) => ({
  width: "100%",
  height: "80px",
  objectFit: "cover",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "opacity 0.2s",
  "&:hover": {
    opacity: 0.8
  }
}));

export const DesktopPropertyTitle = styled(Typography)(({ theme }) => ({
  fontSize: "32px",
  fontWeight: 700,
  lineHeight: "38px",
  color: "#331860",
  marginBottom: "16px"
}));

export const DesktopPropertyFeatures = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "24px",
  marginBottom: "24px",
  flexWrap: "wrap"
}));

export const DesktopFeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "16px",
  fontWeight: 500,
  color: "#331860"
}));

export const DesktopInfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  borderRadius: "12px",
  padding: "20px",
  marginBottom: "16px"
}));

export const DesktopInfoCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  fontWeight: 700,
  color: "#331860",
  marginBottom: "12px",
  display: "flex",
  alignItems: "center",
  gap: "8px"
}));

export const DesktopInfoCardContent = styled(Box)(({ theme }) => ({
  fontSize: "14px",
  color: "#625783",
  lineHeight: "1.5"
}));

// Mobile Layout Container
export const MobileLayoutContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  [theme.breakpoints.up('md')]: {
    display: "none"
  }
}));

export const PropertyImage = styled('img')(({ theme }) => ({
  marginTop: "12px",
  width: "100%",
  height: "200px",
  objectFit: "cover"
}));

export const ContentWrapper = styled(Box)<{ isPhone: boolean }>(({ isPhone, theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  width: "100%"
}));

export const StyledTabs = styled(Tabs)({
  marginTop: "4px",
  borderBottom: "0.5px solid #8E98A1",
  width: "100%",
  "& .MuiButtonBase-root": {
    fontSize: "12px",
    padding: "8px",
    fontWeight: "bold",
  },
  "&.Mui-selected": {
    color: "green",
  },
});

export const StyledTab = styled(Tab)({
  "& .Mui-selected": {
    color: "green",
  },
});

export const TabContent = styled(Box)({
  marginBottom: "80px",
  paddingLeft: "24px",
  paddingRight: "24px",
});

// ==============================
// Property Details Section Styles
// ==============================

export const PropertyDetailsContainer = styled(Box)({});

export const PropertyTitle = styled(Typography)(({ theme }) => ({
  fontSize: "24px",
  fontWeight: 700,
  lineHeight: "28.8px",
  color: "#331860",
  marginTop: "24px",
  marginBottom: "24px",
  [theme.breakpoints.up('md')]: {
    fontSize: "28px",
    lineHeight: "34px"
  }
}));

export const PropertyDivider = styled(Box)({
  border: "1px solid #E2E8F0",
});

// ==============================
// Check-in/Check-out Details Styles
// ==============================

export const CheckInOutContainer = styled(Box)(({ theme }) => ({
  paddingBottom: "4px",
  paddingTop: "12px",
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.up('md')]: {
    padding: "20px 24px"
  }
}));

export const CheckInOutItem = styled(Box)({});

export const CheckInOutLabel = styled(Typography)({
  fontWeight: 700,
  fontSize: "10px",
  lineHeight: "12px",
  color: "#331860",
});

export const CheckInOutDate = styled(Typography)({
  fontWeight: 700,
  fontSize: "13.7px",
  lineHeight: "18px",
  color: "#331860",
  marginTop: "5.6px",
  marginBottom: "5.6px",
});

export const CheckInOutTime = styled(Typography)({
  fontWeight: 400,
  fontSize: "10px",
  lineHeight: "18px",
  color: "#625783",
});

// ==============================
// Map Section Styles
// ==============================

export const MapContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const MapFrame = styled("iframe")(({ theme }) => ({
  frameBorder: 0,
  width: "101%",
  height: "190px",
  borderRadius: "10px",
  [theme.breakpoints.up('md')]: {
    height: "250px"
  }
}));

export const MapHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
  marginTop: "24px",
});

export const MapTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "19.2px",
  color: "#331860",
  marginBottom: "8px",
});

export const MapAddress = styled(Typography)({
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "14.4px",
  color: "#331860",
  textAlign: "center",
  marginTop: "16px",
  marginBottom: "16px",
  opacity: 0.5,
});

export const MapButton = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  color: "white",
  fontWeight: 700,
  lineHeight: "24px",
  fontSize: "16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "48px",
  width: "100%",
  backgroundColor: "#5B3FFF",
  borderRadius: "8px",
  marginTop: "12px",
  [theme.breakpoints.up('md')]: {
    height: "56px",
    fontSize: "18px"
  }
}));

export const MapImageContainer = styled(Box)({
  borderRadius: "8px",
  overflow: "hidden",
  marginBottom: "12px",
});

// ==============================
// Passcode Section Styles
// ==============================

export const PasscodeContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const PasscodeHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const PasscodeTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "19.2px",
  color: "#331860",
  marginBottom: "8px",
});

export const PasscodeDescription = styled(Typography)({
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "14.4px",
  color: "#331860",
  textAlign: "center",
  marginTop: "16px",
  marginBottom: "16px",
});

export const PasscodeBox = styled(Box)({
  paddingLeft: "16px",
  paddingRight: "16px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  paddingTop: "16px",
  paddingBottom: "16px",
});

export const PasscodeText = styled(Typography)<{ hasLock: boolean; codeLength: number }>(
  ({ hasLock, codeLength }) => ({
    fontWeight: 900,
    fontSize: "12px",
    lineHeight: "14.4px",
    color: "#625783",
    textAlign: "center",
    letterSpacing: hasLock && codeLength > 1 ? 10 : undefined,
  })
);

// ==============================
// WiFi Section Styles
// ==============================

export const WifiContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const WifiHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
  marginTop: "24px",
});

export const WifiTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "19.2px",
  color: "#331860",
  marginBottom: "8px",
});

export const WifiSubContainer = styled(Box)({
  border: "1px solid var(--Border, #E2E8F0)",
  borderRadius: "8px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: "12px",
  paddingBottom: "12px",
  paddingLeft: "16px",
  paddingRight: "16px",
});

export const WifiText = styled(Typography)({
  color: "#625783",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
});

export const WifiSpacer = styled(Box)({
  height: "10px",
});

// ==============================
// Location Details Styles
// ==============================

export const LocationContainer = styled(Box)(({ theme }) => ({
  paddingTop: "13.6px",
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const LocationHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const LocationTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "23px",
  color: "#100A55",
  marginLeft: "4.8px",
  marginRight: "4.8px",
});

export const LocationAddress = styled(Typography)({
  color: "#8E98A1",
  fontSize: "12px",
  fontWeight: 500,
});

export const LocationDetailsRow = styled(Box)({
  display: "flex",
  marginTop: "12px",
});

export const LocationDetailsGroup = styled(Box)({
  display: "flex",
});

export const LocationItem = styled(Box)({
  marginLeft: "8px",
  marginRight: "8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

export const LocationItemLabel = styled(Typography)({
  color: "#100A55",
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "24px",
});

export const LocationItemValue = styled(Typography)({
  color: "#100A55",
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "24px",
});

// ==============================
// Instructions Section Styles
// ==============================

export const InstructionsContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const InstructionsHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const InstructionsTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "23px",
  color: "#100A55",
  marginLeft: "3.2px",
  marginRight: "3.2px",
});

export const InstructionPoint = styled(Typography)({
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "15px",
  color: "#331860",
  opacity: 0.4,
  marginBottom: "3.2px",
});

export const InstructionBullet = styled(Box)({
  fontSize: "18px",
  component: "span",
});

// ==============================
// Image Gallery Styles
// ==============================

export const ImageGalleryContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const ImageGalleryHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const ImageGalleryTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "23px",
  color: "#100A55",
  marginLeft: "3.2px",
  marginRight: "3.2px",
});

export const ImageGalleryGrid = styled(Box)({
  display: "flex",
  flexFlow: "wrap",
});

export const ImageGalleryItem = styled(Box)({
  marginLeft: "8px",
  marginRight: "8px",
});

export const GalleryImage = styled('img')(({ theme }) => ({
  width: "163.5px",
  height: "163.5px",
  borderRadius: "8px",
  [theme.breakpoints.up('md')]: {
    width: "200px",
    height: "200px"
  }
}));

// ==============================
// Contact Section Styles
// ==============================

export const ContactContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginBottom: "24px",
  marginTop: "16px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  paddingTop: "16px",
  paddingBottom: "16px",
  justifyContent: "space-between",
  [theme.breakpoints.up('md')]: {
    padding: "24px",
    gap: "16px"
  }
}));

export const ContactButton = styled(Box)(({ theme }) => ({
  width: "49%",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  cursor: "pointer",
  [theme.breakpoints.up('md')]: {
    padding: "24px 16px",
    minHeight: "120px"
  }
}));

export const ContactButtonText = styled(Typography)({
  marginTop: "8px",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
  color: "#625783",
});

// ==============================
// Desktop Layout Specific Styles
// ==============================

// Gallery Image with Selection Border
export const DesktopGalleryImageSelected = styled(DesktopGalleryImage)<{ $isSelected: boolean }>(({ $isSelected }) => ({
  border: $isSelected ? "2px solid #5B3FFF" : "none"
}));

// Check-in/Check-out Section Styles
export const CheckInOutRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "8px"
});

export const CheckInOutTimeRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between"
});

// WiFi Section Styles
export const WifiRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "8px"
});

export const WifiRowLast = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
});

export const WifiIcon = styled(Box)({
  width: "20px",
  height: "20px"
});

export const CopyIcon = styled(Box)({
  cursor: "pointer",
  width: "16px",
  height: "16px"
});

// Access Code Section Styles
export const AccessCodeBox = styled(Box)<{ $codeLength: number }>(({ $codeLength }) => ({
  backgroundColor: "white",
  padding: "12px",
  borderRadius: "8px",
  textAlign: "center",
  fontSize: "18px",
  fontWeight: "bold",
  letterSpacing: $codeLength > 1 ? "normal" : "normal"
}));

export const AccessCodeIcon = styled(Box)({
  width: "20px",
  height: "20px"
});

// Instructions Section Styles
export const InstructionItem = styled(Box)({
  marginBottom: "8px"
});

export const InstructionsIcon = styled(Box)({
  width: "20px",
  height: "20px"
});

// Location Details Section Styles
export const LocationIcon = styled(Box)({
  width: "20px",
  height: "20px",
  marginTop: "-0.3px"
});

// Contact Section Styles
export const ContactRow = styled(Box)({
  display: "flex",
  gap: "12px"
});

export const ContactButtonStyled = styled(ContactButton)({
  width: "48%"
});

export const ContactIcon = styled(Box)({
  width: "24px",
  height: "24px"
});

export const ContactSpacer = styled(Box)({
  width: "15px",
});

// ==============================
// Facility Section Styles
// ==============================

export const FacilityContainer = styled(Box)({
  border: "1px solid #ECECEC",
  borderRadius: "8px",
  height: "64px",
  width: "76px",
  display: "flex",
  justifyContent: "space-between",
  paddingTop: "6.4px",
  paddingBottom: "4px",
  alignItems: "center",
  flexDirection: "column",
  marginLeft: "4px",
  marginRight: "4px",
});

export const FacilityText = styled(Typography)({
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "17px",
  color: "#100A55",
});

// ==============================
// Modal Styles
// ==============================

export const ModalContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: "16px",
  paddingBottom: "16px",
});

export const ModalContactButton = styled(Box)({
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  cursor: "pointer",
});

export const ModalContactText = styled(Typography)({
  marginTop: "8px",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
  color: "#625783",
});

export const ModalBackButton = styled(Box)({
  marginTop: "16px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "8px",
  paddingBottom: "8px",
  cursor: "pointer",
});

export const ModalBackText = styled(Typography)({
  marginTop: "8px",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
  color: "#625783",
});

// ==============================
// Passcode Modal Styles
// ==============================

export const PasscodeModalContainer = styled(Box)({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "4px",
  marginBottom: "4px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
});

export const PasscodeModalHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const PasscodeModalTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "23px",
  color: "#100A55",
  marginLeft: "8px",
  marginRight: "8px",
});

export const PasscodeModalDescription = styled(Typography)({
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "14.4px",
  color: "#331860",
  textAlign: "center",
  marginTop: "16px",
  marginBottom: "16px",
  opacity: 0.4,
});

export const PasscodeModalBox = styled(Box)({
  paddingLeft: "16px",
  paddingRight: "16px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  paddingTop: "16px",
  paddingBottom: "16px",
});

export const PasscodeModalText = styled(Typography)<{ codeLength: number }>(({ codeLength }) => ({
  fontWeight: 900,
  fontSize: "12px",
  lineHeight: "14.4px",
  color: "#625783",
  textAlign: "center",
  letterSpacing: codeLength > 1 ? 20 : undefined,
  marginTop: "4px",
}));

export const PasscodeModalBackButton = styled(Box)({
  marginTop: "16px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  cursor: "pointer",
});

export const PasscodeModalBackText = styled(Typography)({
  marginTop: "0px",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
  color: "#625783",
});

export const Title = styled('span')<TextProps>`
  font-weight: 700;
  font-style: bold;
  font-size: ${({ $isPhone }) => (!$isPhone ? "32px" : "24px")};
  color: #09090b;
  text-align: center;
`;

export const Description = styled('span')<TextProps>`
  font-weight: 400;
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "16px")};
  text-align: center;
  color: #71717a;
  margin-inline: ${({ $isPhone }) => (!$isPhone ? "0px" : "25px")};
  padding-inline: ${({ $isPhone }) => (!$isPhone ? "32px" : "16px")};
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
`;

export const InfoBox = styled('span')<TextProps>`
  font-weight: 400;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "14px")};
  text-align: center;
  color: #09090b;
  margin-inline: ${({ $isPhone }) => (!$isPhone ? "0px" : "25px")};
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  padding: ${({ $isPhone }) => (!$isPhone ? "24px" : "16px")};
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
`;

export const StyledButton = styled('button')<ButtonProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "300px" : "100%")};
  height: ${({ $isPhone }) => (!$isPhone ? "56px" : "48px")};
  border-radius: 10.8px;
  background-color: #5b3fff;
  color: #fff;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "14px")};
  font-weight: 500;
  font-style: bold;
  text-align: center;
  gap: 8px;
  padding: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Badge = styled('div')<BadgeProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background-color: #f9f9fa;
  padding: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  border-radius: 12px;
  left: ${({ $isPhone }) => (!$isPhone ? "50%" : "auto")};
  margin-block: 20px;
`;

export const BadgeText = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "12px")};
  font-weight: 400;
  color: #00000075;
`;

export const SuccessIcon = styled(SuccessPaymentIcon)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
  height: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
`;

export const FailPaymentIcon = styled(FailPayment)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
  height: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
`;

export const ArrowIcon = styled(ArrowBack)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  height: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
`;

export const ReTryIcon = styled(ReTry)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  height: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
`;

export const MsoolIcon = styled(MsoolVector)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "64px" : "54px")};
  height: ${({ $isPhone }) => (!$isPhone ? "25px" : "21px")};
`;

// ==============================
// Payment page shell (MS-533)
// ==============================
//
// Figma node 2563:8346 is a light-grey page holding a single 560px column of white cards.
// The previous shell inverted that — white page, grey cards — and, worse, centred its
// children without stretching them, so each section shrank to its own content width
// (measured on dev at 390px: agreement 390, payment 314, banner 260, badge 216). Laying
// the sections out in ONE column with `align-items: stretch` makes them share a width by
// construction rather than by repeating `width: 100%` on every child.

export const PaymentPageShell = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background-color: #f6f6f8;
  padding-inline: ${({ $isPhone }) => (!$isPhone ? "16px" : "12px")};
  padding-bottom: ${({ $isPhone }) => (!$isPhone ? "48px" : "32px")};
`;

export const PaymentPageLogo = styled('div')<ContainerProps>`
  display: flex;
  justify-content: center;
  margin-block: ${({ $isPhone }) => (!$isPhone ? "40px" : "28px")};

  img {
    width: ${({ $isPhone }) => (!$isPhone ? "130px" : "112px")};
    height: auto;
  }
`;

// The single column every section belongs to. `stretch` is load-bearing: it is what keeps
// the hero, the date card, the invoice, the agreement row, the payment card and the banner
// on one left and right edge at every viewport.
export const DetailsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  gap: 18px;
`;

export const ImageContainer = styled('div')<ContainerProps>`
  width: 100%;
  overflow: hidden;
  border-radius: 16px;
  /* Figma's hero is 560x190. Holding the ratio keeps it proportional as the column
     narrows, and the floor stops it collapsing to a 124px sliver on a phone. */
  aspect-ratio: 560 / 190;
  min-height: ${({ $isPhone }) => (!$isPhone ? "190px" : "170px")};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

// Title + features sit directly on the page background in Figma, not inside a card.
export const ContentContainer = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding-inline: 2px;
`;

/** White card — the shared surface for the date, invoice, agreement and payment blocks. */
export const PaymentPageCard = styled('div')<ContainerProps>`
  width: 100%;
  background-color: #ffffff;
  border-radius: 16px;
  padding: ${({ $isPhone }) => (!$isPhone ? "18px" : "16px")};
`;

export const MainTitle = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "26px" : "22px")};
  font-weight: 700;
  color: #141316;
`;

export const FeaturesContainer = styled('div')<ContainerProps>`
  display: flex;
  align-items: center;
  /* Wraps instead of overflowing when a property has long feature labels. */
  flex-wrap: wrap;
  gap: ${({ $isPhone }) => (!$isPhone ? "24px" : "16px")};
  padding-top: ${({ $isPhone }) => (!$isPhone ? "10px" : "8px")};
`;

export const FeatureItem = styled('span')<TextProps>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "12px")};
  font-weight: 400;
  color: #222222cc;
`;

export const DateContainer = styled('div')<ContainerProps>`
  display: flex;
  width: 100%;
  padding-block: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  background-color: #ffffff;
  border-radius: 16px;
  justify-content: space-between;
  align-items: center;
  /* MS-533: 40px of inline padding plus a fixed-width connector left ~94px per date
     column inside the portal's 500px shell, which wrapped "تسجيل الدخول" and
     "13 أغسطس" onto two lines. The columns now size to their text and the connector
     absorbs whatever is left. */
  padding-inline: ${({ $isPhone }) => (!$isPhone ? "24px" : "16px")};
  gap: ${({ $isPhone }) => (!$isPhone ? "16px" : "10px")};
`;

export const DateItem = styled('span')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ $isPhone }) => (!$isPhone ? "8px" : "6px")};
  /* Never let a label or a date break mid-phrase — the connector shrinks instead. */
  flex: 0 0 auto;
  white-space: nowrap;
`;

export const DateLabel = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "15px" : "10px")};
  font-weight: 700;
  color: #331860;
`;

export const DateValue = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 700;
  color: #331860;
`;

export const DateSubtext = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "15px" : "10px")};
  font-weight: 400;
  color: #625783;
`;

export const LineIconContainer = styled('span')`
  display: flex;
  align-items: center;
  justify-content: center;
  /* MS-533: the connector is the flexible element between the two date columns.
     min-width: 0 lets it shrink below its intrinsic size on narrow screens
     instead of forcing the columns to wrap. */
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;

  svg {
    width: 100%;
    min-width: 0;
  }
`;

export const PaymentContainer = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  padding: ${({ $isPhone }) => (!$isPhone ? "18px" : "16px")};
  background-color: #ffffff;
  border-radius: 16px;
  gap: ${({ $isPhone }) => (!$isPhone ? "14px" : "12px")};
`;

export const PaymentTitle = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "15px")};
  font-weight: 700;
  color: #5b2ff9;
`;

export const PaymentRow = styled('span')`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

export const PaymentLabel = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "15px" : "14px")};
  font-weight: 500;
  color: #435363;
`;

export const PaymentValue = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "15px" : "14px")};
  font-weight: 400;
  color: #8e98a1;
`;

export const PaymentTotal = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "15px")};
  font-weight: 700;
  color: #141316;
`;

/** The one figure the guest is here to read — Figma sets it large and purple. */
export const PaymentTotalAmount = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "26px" : "22px")};
  font-weight: 700;
  color: #5b2ff9;
`;

// Payment section without padding
export const PaymentSection = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  gap: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
`;

// PaymentSuccessDetails specific styled components
export const DetailsSection = styled('section')<ContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ $isPhone }) => (!$isPhone ? "24px" : "16px")};
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
  width: 100%;
`;

export const PropertyInfoContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

export const PropertyTextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: start;
`;

export const PropertyTitleSpan = styled('span')`
  font-size: 20px;
  font-weight: 700;
  color: #000000;
`;

export const PropertyLocation = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #000000b2;
`;

export const BookingDetailsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

export const BookingDetailsTitle = styled('span')`
  font-size: 14px;
  font-weight: 500;
  color: #000000;
`;

export const BookingDetailsValue = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
`;

export const BookingGuestsText = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// PaymentSuccessDetails.tsx styled components
export const PaymentSuccessContainer = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  gap: 16px;
  padding: 30px 16px 16px 16px;
  position: relative;
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
  margin: ${({ $isPhone }) => (!$isPhone ? "0 auto" : "0")};
`;

export const PaymentSuccessTitle = styled('span')<TextProps>`
  font-weight: 700;
  font-style: bold;
  font-size: ${({ $isPhone }) => (!$isPhone ? "32px" : "24px")};
  color: #09090b;
  text-align: center;
`;

export const PaymentSuccessDescription = styled('span')<TextProps>`
  font-weight: 400;
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "16px")};
  text-align: center;
  color: #71717a;
  margin-inline: ${({ $isPhone }) => (!$isPhone ? "0px" : "25px")};
  padding-inline: ${({ $isPhone }) => (!$isPhone ? "32px" : "16px")};
  width: 100%;
`;

export const PaymentSuccessInfoBox = styled('span')<TextProps>`
  font-weight: 400;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "14px")};
  text-align: center;
  color: #09090b;
  margin-inline: ${({ $isPhone }) => (!$isPhone ? "0px" : "25px")};
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  padding: ${({ $isPhone }) => (!$isPhone ? "24px" : "16px")};
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
`;

export const PaymentSuccessButton = styled('button')<ButtonProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "300px" : "100%")};
  height: ${({ $isPhone }) => (!$isPhone ? "56px" : "48px")};
  border-radius: 10.8px;
  background-color: #5b3fff;
  color: #fff;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "14px")};
  font-weight: 500;
  font-style: bold;
  text-align: center;
  gap: 8px;
  padding: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// MS-595: `margin-top: auto`, not `position: absolute; bottom: 30px`.
//
// The old rule pinned this 30px above PaymentSuccessContainer's bottom edge, which reads as a
// footer only while the container is exactly its 100vh minimum and the content leaves empty
// space beneath. Once the content is taller than the viewport the container grows to fit, the
// CTA button becomes the last flow element at that bottom edge, and the badge was drawn on top
// of it — 42px of overlap on a 695px-tall window, on any booking whose content overflows.
//
// As a flex item with an auto top margin it gets the same behaviour honestly: pushed to the
// bottom of the free space when content is short, placed after the button when it is not.
// The container already centres its children, so the left/transform hack is not needed.
export const PaymentSuccessBadge = styled('div')<BadgeProps>`
  margin-top: auto;
  margin-bottom: ${({ $isPhone }) => (!$isPhone ? "30px" : "13px")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background-color: #f9f9fa;
  padding: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  border-radius: 12px;
`;

export const PaymentSuccessBadgeText = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "12px")};
  font-weight: 400;
  color: #00000075;
`;

export const PaymentSuccessIcon = styled(SuccessPaymentIcon)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
  height: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
`;

export const PaymentSuccessArrowIcon = styled(ArrowBack)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  height: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
`;

export const PaymentSuccessMsoolIcon = styled(MsoolVector)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "64px" : "54px")};
  height: ${({ $isPhone }) => (!$isPhone ? "25px" : "21px")};
`;

export const PaymentSuccessFeaturesContainer = styled('div')<ContainerProps>`
  display: flex;
  align-items: center;
  gap: ${({ $isPhone }) => (!$isPhone ? "24px" : "20px")};
  padding-bottom: ${({ $isPhone }) => (!$isPhone ? "24px" : "21px")};
  padding-top: ${({ $isPhone }) => (!$isPhone ? "12px" : "10px")};
`;

export const PaymentSuccessFeatureItem = styled('span')<TextProps>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "12px")};
  font-weight: 400;
  color: #222222cc;
`;

export const PaymentSuccessPaymentContainer = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  gap: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
`;

export const PaymentSuccessPaymentTitle = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  font-weight: 500;
  color: #130080;
`;

export const PaymentSuccessPaymentRow = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

export const PaymentSuccessPaymentLabel = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 500;
  color: #435363;
`;

export const PaymentSuccessPaymentValue = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 400;
  color: #8e98a1;
`;

export const PaymentSuccessPaymentTotal = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 500;
  color: #222222;
`;

export const PaymentSuccessDetailsSection = styled('section')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
`;

export const PaymentSuccessPropertyInfoContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

export const PaymentSuccessPropertyTextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: start;
`;

export const PaymentSuccessPropertyTitle = styled('span')`
  font-size: 20px;
  font-weight: 700;
  color: #000000;
`;

export const PaymentSuccessPropertyLocation = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #000000b2;
`;

export const PaymentSuccessBookingDetailsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

export const PaymentSuccessBookingDetailsTitle = styled('span')`
  font-size: 14px;
  font-weight: 500;
  color: #000000;
`;

export const PaymentSuccessBookingDetailsText = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
`;

export const PaymentSuccessBookingGuestsText = styled('span')` 
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const PaymentSuccessDivider = styled('div')`
  width: 100%;
  margin-block: 16px;
  height: 1px;
  background-color: #e0e0e0;
`;

export const PaymentSuccessPaymentDivider = styled('div')`
  width: 100%;
  height: 1px;
  background-color: #dddddd;
`;

// GuestPortalDetails.tsx styled components
export const GuestPortalDetailsIconStyled = styled(GuestPortalDetailsIconSvg)`
  width: 100%;
  height: 100%;
`;

export const GuestPortalDetailsDivider = styled('div')`
  width: 100%;
  height: 1px;
  margin-bottom: 24px;
  background-color: #dddddd;
`;

export const GuestPortalDetailsDateDivider = styled('div')`
  width: 100%;
  height: 1px;
  margin-block: 24px;
  background-color: #dddddd;
`;

export const GuestPortalDetailsPaymentDivider = styled('div')`
  width: 100%;
  height: 1px;
  background-color: #dddddd;
`;

export const PaymentMethodsSection = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 16px;
`;

export const PaymentMethodsTitle = styled('span')`
  color: #5b2ff9;
  font-size: 15px;
  font-weight: 700;
  line-height: normal;
  padding-bottom: 14px;
`;

export const PaymentMethodsList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const PaymentFormSection = styled('div')`
  width: 100%;
  margin-top: 18px;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 16px;
`;

export const PaymentFormTitle = styled('span')`
  color: #5b2ff9;
  font-size: 15px;
  font-weight: 700;
  line-height: normal;
  padding-bottom: 14px;
`;

// MS-605. The Pay button is Moyasar's, rendered inside this container by their bundle, and
// their stylesheet pins it to a muted default that reads as disabled: a guest with the agreement
// signed, a method chosen and the card filled in still saw a greyed button and did not press it.
// Nothing in this app styled it, so there was no enabled state to tell from the disabled one.
//
// Scoped to this container rather than a global sheet — the Moyasar form is the only thing that
// ever renders inside it. Both the `bg-primary` class and the submit types are covered because
// the bundle has used each across versions, and `!important` because their rules carry it too.
export const PaymentFormContainer = styled('div')`
  display: flex;
  flex-direction: column;

  /* Every selector below starts at div#mysr: Moyasar's own rule is
     div#mysr .bg-primary { background-color: rgb(118 141 255) !important }, and an !important
     rule with an ID in it beats ours without one — which is why this purple never rendered. */
  & div#mysr button[type="submit"],
  & div#mysr input[type="submit"],
  & div#mysr button.bg-primary,
  & div#mysr input.bg-primary {
    background-color: #5b2ff9 !important;
    border-color: #5b2ff9 !important;
    color: #ffffff !important;
    /* Moyasar ships Tailwind: without this their opacity variable keeps washing the fill out. */
    --tw-bg-opacity: 1 !important;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.15s ease-in-out;
  }

  /* The amount is a span and inherits Moyasar's ink, not ours. */
  & div#mysr button[type="submit"] span,
  & div#mysr button[type="submit"] .currency-amount {
    color: #ffffff !important;
  }

  /* The riyal mark is NOT text: Moyasar renders it as an <img> of a BLACK glyph, so no colour
     reaches it. Their own sheet is what tints it — div#mysr .currency-icon { filter: brightness(0)
     invert(1) }. This rule used to clear that filter, which handed the glyph back to black beside
     the white amount (MS-605; same fix as the direct booking website). Restated here so the white
     ink matches the amount whatever Moyasar's sheet does next. */
  & div#mysr button[type="submit"] .currency-icon {
    filter: brightness(0) invert(1) !important;
  }

  & div#mysr button[type="submit"]:hover:not(:disabled),
  & div#mysr button.bg-primary:hover:not(:disabled) {
    background-color: #4a21e0 !important;
    border-color: #4a21e0 !important;
  }

  /* Moyasar disables its own button while a payment is in flight. That state has to stay
     legibly different from the enabled one — which is the half of MS-605 this block exists for. */
  & div#mysr button[type="submit"]:disabled,
  & div#mysr button.bg-primary:disabled {
    background-color: #c7c2dd !important;
    border-color: #c7c2dd !important;
    cursor: not-allowed;
  }
`;

// PaymentMethodItem Component Styles
//
// The disabled treatment is `opacity` on this row ONLY. PaymentDisabledOverlay used to add a
// second 0.45 layer on top of the 0.6 here, and 0.6 x 0.45 = 0.27 rendered the gated rows
// illegible. Muted text colours plus the grey fill carry the state now.
export const PaymentMethodItemContainer = styled('div')<{ $disabled: boolean }>(({ $disabled, theme }) => `
  display: flex;
  padding: 14px 16px;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  gap: 8px;
  border-radius: 12px;
  border: 1px solid ${$disabled ? "#eceef1" : "#e2e8f0"};
  background-color: ${$disabled ? "#f8f9fa" : "transparent"};

  ${theme.breakpoints.up('md')} {
    padding: 16px 18px;
  }
`);

export const PaymentMethodContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
  margin-inline: 10px;
`;

// Disabled means "not yet available", not "not worth reading" — the guest still needs to know
// which methods exist. #9CA3AF/#D1D5DB on the #f8f9fa fill fell below any usable contrast.
export const PaymentMethodTitle = styled('span')<{ $disabled: boolean }>`
  color: ${({ $disabled }) => ($disabled ? "#6b7280" : "#020817")};
  font-size: 15px;
  font-weight: 700;
`;

export const PaymentMethodDescription = styled('span')<{ $disabled: boolean }>`
  color: ${({ $disabled }) => ($disabled ? "#8b93a1" : "#64748B")};
  font-size: 12px;
  font-weight: 400;
`;

// Payment Method Icon Styles
export const CreditCardIconContainer = styled('div')`
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  padding: 6px;
  background-color: #5b3fff0d;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ApplePayIconContainer = styled('div')`
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  padding: 6px;
  background-color: #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ==============================
// RentalAgreementModal Styles (MS-533)
// ==============================

export const AgreementModalOverlay = styled('div')`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
`;

export const AgreementModalCard = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 640px;
  max-height: 85vh;
  overflow: hidden;
  border-radius: 16px;
  background: #ffffff;
  direction: rtl;
`;

export const AgreementModalHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #eceef1;
`;

export const AgreementModalTitle = styled('h2')`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1a1033;
`;

export const AgreementModalClose = styled('button')`
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: #f4f4f5;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
`;

export const AgreementModalBody = styled('div')`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

export const AgreementHeading = styled('h3')`
  margin: 24px 0 8px;
  font-size: 16px;
  font-weight: 700;
  color: #1a1033;
`;

export const AgreementParagraph = styled('p')`
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.9;
  color: #4f5a68;
`;

export const AgreementStateText = styled('p')`
  margin: 0;
  padding: 24px 0;
  text-align: center;
  font-size: 15px;
  color: #6b7280;
`;

export const AgreementModalFooter = styled('div')`
  padding: 16px 24px 24px;
  border-top: 1px solid #eceef1;
`;

export const AgreementConsentLabel = styled('label')`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
`;

export const AgreementActions = styled('div')`
  display: flex;
  gap: 12px;
  margin-top: 18px;
`;

export const AgreementPrimaryButton = styled('button')`
  padding: 12px 32px;
  border: 0;
  border-radius: 12px;
  background: #5b2ff9;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const AgreementSecondaryButton = styled('button')`
  padding: 12px 32px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  color: #4f5a68;
  font-size: 15px;
  cursor: pointer;
`;

// ==============================
// RentalAgreementRow Styles (MS-533)
// ==============================

export const AgreementRowCard = styled('div')<ContainerProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: ${({ $isPhone }) => (!$isPhone ? "18px" : "16px")};
  border-radius: 16px;
  background: #ffffff;
  direction: rtl;

  /* Below ~480px of content width the title, the badge and the button cannot share a row
     without the button label breaking mid-phrase. Stack instead. */
  ${({ $isPhone }) =>
    $isPhone &&
    `
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  `}
`;

export const AgreementRowLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const AgreementRowTitle = styled('span')`
  font-size: 16px;
  font-weight: 700;
  color: #1a1033;
`;

export const AgreementRequiredBadge = styled('span')`
  padding: 4px 12px;
  border-radius: 20px;
  background: #fdeaee;
  color: #e0396a;
  font-size: 12px;
  font-weight: 700;
`;

export const AgreementOpenButton = styled('button')`
  padding: 12px 24px;
  border: 0;
  border-radius: 12px;
  background: #5b2ff9;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  /* The label is a fixed phrase; it should push the layout rather than break in half. */
  white-space: nowrap;
  flex: 0 0 auto;
`;

export const AgreementSignedPill = styled('span')`
  padding: 10px 20px;
  border-radius: 12px;
  background: #17a34a;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
  flex: 0 0 auto;
`;

export const AgreementBlockedBanner = styled('div')`
  width: 100%;
  padding: 16px 24px;
  border-radius: 12px;
  background: #fdf3e0;
  color: #a1730a;
  font-size: 14px;
  text-align: center;
  direction: rtl;
`;

// Only `pointer-events` — the dimming lives on PaymentMethodItemContainer. Stacking both
// multiplied to an effective 0.27 opacity and made the gated rows unreadable on dev.
export const PaymentDisabledOverlay = styled('div')<{ $disabled: boolean }>(({ $disabled }) => `
  width: 100%;
  pointer-events: ${$disabled ? "none" : "auto"};
`);

// ── MS-591 · signed agreement page ─────────────────────────────────────────────────────────
// The read-only counterpart to the AgreementModal* family above. Same typography (the body is
// rendered by the same parseAgreementBody + AgreementHeading/AgreementParagraph pair), but laid
// out as a page card inside PaymentPageShell rather than as an overlay — no header row, because
// there is no × to put in one.

export const SignedAgreementCard = styled('div')<ContainerProps>`
  width: 100%;
  /* 560px is the shell's column, set by DetailsContainer — the payment page's hero, date card,
     invoice and agreement row all sit on those edges. This page is the same shell, so a wider
     card here would read as a different surface rather than the same one in a signed state. */
  max-width: 560px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 16px;
  padding: ${({ $isPhone }) => ($isPhone ? "20px 16px" : "32px")};
  direction: rtl;
`;

export const SignedAgreementBanner = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

export const SignedAtText = styled('p')`
  margin: 0;
  font-size: 14px;
  line-height: 1.8;
  color: #4f5a68;
`;

export const SignedAgreementTitle = styled('h1')<ContainerProps>`
  margin: 0 0 16px;
  font-size: ${({ $isPhone }) => ($isPhone ? "20px" : "24px")};
  font-weight: 700;
  color: #1a1033;
`;
